import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import UpdateIcon from "@mui/icons-material/Update";
import { MachineDetails } from "./machineDetails";
import {
  SavingFailedAlert,
  SavingSuccessAlert,
} from "../../components/molecules/SavingModal";
import CustomButton from "../../components/molecules/button";
import { AppDispatch, StoreState } from "../../state/configureStore";
import {
  fetchAllSoftwareVersions,
  fetchTargetSoftware,
  setTargetSoftwareVersion,
} from "../../state/thunks/targetSoftwareThunks";
import { padding } from "../../theme";

interface IComponentOwnProps {
  open: boolean;
  closeModal: () => void;
  details: MachineDetails | undefined;
}

function UpdateTargetSoftwareModal(
  props: IComponentOwnProps,
): React.ReactElement {
  const dispatch = useDispatch<AppDispatch>();
  const { list, status: allVersionsStatus } = useSelector(
    (s: StoreState) => s.targetSoftware.allVersions,
  );
  const currentTarget = useSelector((s: StoreState) =>
    props.details?.serialNumber
      ? s.targetSoftware.bySerialId[props.details.serialNumber]?.value
      : undefined,
  );

  const [selectedVersion, setSelectedVersion] = useState<string>("");
  /** null: no result yet, true: command error, false: success */
  const [commandResult, setCommandResult] = useState<boolean | null>(null);
  const [commandSaving, setCommandSaving] = useState(false);
  const [sameVersionWarning, setSameVersionWarning] = useState(false);

  const listLoading = allVersionsStatus === "saving";
  const fetchListFailed = allVersionsStatus === "failed";
  // Intentional empty state after a successful list fetch (not a render bug).
  const emptyListAfterSavedFetch =
    allVersionsStatus === "saved" &&
    !listLoading &&
    !fetchListFailed &&
    list.length === 0;

  const handleClose = () => {
    setCommandResult(null);
    setCommandSaving(false);
    setSameVersionWarning(false);
    props.closeModal();
  };

  useEffect(() => {
    if (props.open) {
      setCommandResult(null);
      setCommandSaving(false);
      setSameVersionWarning(false);
      void dispatch(fetchAllSoftwareVersions());
    }
  }, [props.open, dispatch]);

  useEffect(() => {
    if (!list.length) {
      return;
    }
    if (currentTarget && list.some((r) => r.version === currentTarget)) {
      setSelectedVersion(currentTarget);
    } else {
      setSelectedVersion(list[0].version);
    }
  }, [list, currentTarget]);

  const onConfirm = async () => {
    if (!selectedVersion.trim() || !props.details?.serialNumber) {
      return;
    }
    if (currentTarget && selectedVersion === currentTarget) {
      setSameVersionWarning(true);
      return;
    }
    setSameVersionWarning(false);
    setCommandResult(null);
    setCommandSaving(true);

    try {
      await dispatch(
        setTargetSoftwareVersion({
          serialNumber: props.details.serialNumber,
          deviceId: props.details.deviceId ?? "",
          version: selectedVersion,
        }),
      ).unwrap();
      // Command does not update the slice; refresh read model into `bySerialId`.
      void dispatch(fetchTargetSoftware(props.details.serialNumber));
      setCommandResult(false);
    } catch {
      setCommandResult(true);
    } finally {
      setCommandSaving(false);
    }
  };

  const isSuccess = commandResult === false;

  const successMessage = isSuccess ? (
    <>
      Target software has been set to {selectedVersion}.<br />
      <br />
      Note that you must update the hub to apply the new software to the device.
    </>
  ) : null;

  const onSelectChange = (e: SelectChangeEvent<string>) => {
    setSameVersionWarning(false);
    setSelectedVersion(e.target.value);
  };

  return (
    <Dialog open={props.open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Update target software</DialogTitle>

      <DialogContent>
        <SavingFailedAlert
          savingFailedMessage="Could not load software versions. Try again later."
          show={fetchListFailed}
        />
        <SavingFailedAlert
          savingFailedMessage="Could not update target software. Try again later."
          show={commandResult === true}
        />
        <SavingSuccessAlert
          savingSuccessMessage={successMessage}
          show={isSuccess}
        />
        <SavingFailedAlert
          savingFailedMessage="Selected version matches current target software. Choose another version before saving."
          show={sameVersionWarning}
        />
        {!isSuccess ? (
          <React.Fragment>
            <Typography component="p" variant="body2" sx={{ mb: 2 }}>
              Choose the new target software for the hub.
            </Typography>
            {listLoading && !list.length ? (
              <CircularProgress size={32} sx={{ display: "block", my: 2 }} />
            ) : null}
            {emptyListAfterSavedFetch ? (
              <Typography
                component="p"
                variant="body2"
                color="text.secondary"
                sx={{ mt: 1, mb: 0 }}
              >
                No software versions are available. The list from the service
                was empty—check configuration or that software versions are
                published, then try again.
              </Typography>
            ) : null}
            {!fetchListFailed && (list.length > 0 || listLoading) ? (
              <FormControl
                fullWidth
                margin="none"
                disabled={listLoading || commandSaving}
                sx={{ mt: padding / 2 }}
              >
                <InputLabel id="target-software-select-label">
                  Target software
                </InputLabel>
                <Select<string>
                  labelId="target-software-select-label"
                  label="Target software"
                  value={selectedVersion}
                  onChange={onSelectChange}
                  renderValue={(v) => {
                    const row = list.find((r) => r.version === v);
                    if (!row) {
                      return v;
                    }
                    return row.isDefault
                      ? `${row.version} (Default)`
                      : row.version;
                  }}
                >
                  {list.map((row, index) => (
                    <MenuItem
                      key={`${row.version}-${String(index)}`}
                      value={row.version}
                      sx={
                        row.isDefault
                          ? {
                              fontWeight: 600,
                              bgcolor: "action.selected",
                            }
                          : undefined
                      }
                    >
                      {row.isDefault
                        ? `${row.version} (Default)`
                        : row.version}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : null}
          </React.Fragment>
        ) : null}
      </DialogContent>

      <DialogActions>
        {isSuccess ? (
          <CustomButton onClick={handleClose}>Close</CustomButton>
        ) : (
          <React.Fragment>
            <CustomButton outlined onClick={handleClose}>
              Cancel
            </CustomButton>
            <LoadingButton
              disabled={
                !selectedVersion.trim() ||
                listLoading ||
                fetchListFailed ||
                !list.length ||
                commandSaving
              }
              loading={commandSaving}
              onClick={() => {
                void onConfirm();
              }}
              loadingPosition="start"
              startIcon={<UpdateIcon />}
              variant="contained"
            >
              Change
            </LoadingButton>
          </React.Fragment>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default UpdateTargetSoftwareModal;
